import { loadDispatchShipments } from '@/features/kitchen/storage/dispatchStorage'
import {
  kitchenTodayCompact,
  kitchenTodayISO,
  kitchenYesterdayISO,
} from '@/features/kitchen/lib/kitchenDates'
import { loadJson, saveJson } from '@/features/kitchen/storage/persist'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { isMockApi } from '@/shared/env'

export const DELMAN_TRIP_STORAGE_KEY = 'superoti.delman.trip'

export type DeliveryStopStatus = 'pending' | 'qr_shown' | 'receiving_draft' | 'receiving_completed'

export type ReceivingStatus = 'none' | 'draft' | 'completed'

export type DeliveryLine = {
  productName: string
  productionDate: string
  qtyShipped: number
  qtyReceived?: number
}

export type DeliveryStop = {
  id: string
  storeName: string
  order: number
  status: DeliveryStopStatus
  receivingStatus: ReceivingStatus
  lines: DeliveryLine[]
  qrToken?: string
  qrExpiresAt?: string
  qrShownAt?: string
  receivingDraftAt?: string
}

export type ReturnLineItem = {
  productName: string
  productionDate: string
  qty: number
}

export type ReturnPickupDoc = {
  id: string
  returnCode: string
  storeName: string
  itemCount: number
  destination: 'to_kitchen'
  status: 'waiting_scan' | 'picked_up'
  pickedUpAt?: string
  lines: ReturnLineItem[]
}

export type TripStatus = 'ready' | 'in_progress' | 'completed'

export type TripRecord = {
  id: string
  delmanName: string
  kitchenName: string
  date: string
  status: TripStatus
  stops: DeliveryStop[]
  returnPickups: ReturnPickupDoc[]
  startedAt?: string
  /** Toko yang dipilih saat pertama kali mulai trip. */
  startedViaStopId?: string
  completedAt?: string
  transitRemainderQty?: number
}

function todayCompact(): string {
  return kitchenTodayCompact()
}

function todayProdDateLabel(): string {
  const iso = kitchenTodayISO()
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y.slice(2)}`
}

const DEMO_STORE_NAMES = new Set(['Toko Menteng', 'Toko Kemang', 'Toko Cipete'])

/** Trip contoh lokal (hanya VITE_MOCK_API=true). */
function buildDemoStops(): DeliveryStop[] {
  const suffix = todayCompact()
  const prod = todayProdDateLabel()
  return [
    {
      id: `DLV-${suffix}-01`,
      storeName: 'Toko Menteng',
      order: 1,
      status: 'pending',
      receivingStatus: 'none',
      lines: [
        { productName: 'Roti coklat', productionDate: prod, qtyShipped: 50 },
        { productName: 'Donat meses', productionDate: prod, qtyShipped: 30 },
      ],
    },
    {
      id: `DLV-${suffix}-02`,
      storeName: 'Toko Kemang',
      order: 2,
      status: 'pending',
      receivingStatus: 'none',
      lines: [
        { productName: 'Roti coklat', productionDate: prod, qtyShipped: 40 },
        { productName: 'Roti keju', productionDate: prod, qtyShipped: 20 },
      ],
    },
    {
      id: `DLV-${suffix}-03`,
      storeName: 'Toko Cipete',
      order: 3,
      status: 'pending',
      receivingStatus: 'none',
      lines: [
        { productName: 'Donat gula', productionDate: prod, qtyShipped: 25 },
        { productName: 'Roti coklat', productionDate: prod, qtyShipped: 15 },
      ],
    },
  ]
}

function todayTripId(): string {
  return `TRP-KIT-${todayCompact()}`
}

export function todayDateKey(): string {
  return kitchenTodayISO()
}

export function isDemoTrip(trip: TripRecord): boolean {
  return trip.stops.some((stop) => DEMO_STORE_NAMES.has(stop.storeName))
}

export function createEmptyTodayTrip(delmanName: string, kitchenName: string): TripRecord {
  return {
    id: todayTripId(),
    delmanName,
    kitchenName,
    date: todayDateKey(),
    status: 'ready',
    stops: [],
    returnPickups: [],
  }
}

function buildDemoReturnPickups(): ReturnPickupDoc[] {
  const code = `RTN-OTL-${todayCompact().slice(-4)}-01`
  return [
    {
      id: code,
      returnCode: code,
      storeName: 'Toko Menteng',
      itemCount: 5,
      destination: 'to_kitchen',
      status: 'waiting_scan',
      lines: [
        { productName: 'Roti coklat', productionDate: todayCompact(), qty: 3 },
        { productName: 'Donat meses', productionDate: todayCompact(), qty: 2 },
      ],
    },
  ]
}

function mapShipmentStatus(
  shipmentStatus: string,
): { stopStatus: DeliveryStopStatus; receivingStatus: ReceivingStatus } {
  if (shipmentStatus === 'received') {
    return { stopStatus: 'receiving_completed', receivingStatus: 'completed' }
  }
  if (shipmentStatus === 'in_transit' || shipmentStatus === 'prepared') {
    return { stopStatus: 'pending', receivingStatus: 'none' }
  }
  return { stopStatus: 'pending', receivingStatus: 'none' }
}

const TRIP_ASSIGNABLE_STATUSES = new Set(['prepared', 'in_transit', 'received'])

export function buildTripFromDispatch(delmanName: string, kitchenName: string): TripRecord {
  const shipments = loadDispatchShipments().filter(
    (s) => s.delmanName === delmanName && TRIP_ASSIGNABLE_STATUSES.has(s.status),
  )

  const stops: DeliveryStop[] =
    shipments.length > 0
      ? shipments.map((shipment, index) => {
          const mapped = mapShipmentStatus(shipment.status)
          const received = shipment.status === 'received'
          return {
            id: shipment.id,
            storeName: shipment.storeName,
            order: index + 1,
            status: received ? 'receiving_completed' : mapped.stopStatus,
            receivingStatus: received ? 'completed' : mapped.receivingStatus,
            lines: shipment.lines
              .filter((line) => line.qty > 0)
              .map((line) => ({
                productName: line.productName,
                productionDate: line.productionDate,
                qtyShipped: line.qty,
                qtyReceived: received ? line.qty : undefined,
              })),
          }
        })
      : isMockApi
        ? buildDemoStops()
        : []

  const returnPickups = isMockApi ? buildDemoReturnPickups() : []

  return {
    id: todayTripId(),
    delmanName,
    kitchenName,
    date: todayDateKey(),
    status: stops.length > 0 ? 'ready' : 'ready',
    stops,
    returnPickups,
  }
}

export function loadTripRecord(fallback: TripRecord): TripRecord {
  return loadJson<TripRecord>(DELMAN_TRIP_STORAGE_KEY) ?? fallback
}

export function saveTripRecord(trip: TripRecord): void {
  saveJson(DELMAN_TRIP_STORAGE_KEY, trip)
}

const DELMAN_LAST_TRIP_PREFIX = 'superoti.delman.trip.last.'

export function saveLastCompletedTrip(trip: TripRecord): void {
  saveJson(`${DELMAN_LAST_TRIP_PREFIX}${trip.delmanName}`, {
    ...trip,
    status: 'completed' as const,
  })
}

export function loadLastCompletedTrip(delmanName: string): TripRecord | null {
  return loadJson<TripRecord>(`${DELMAN_LAST_TRIP_PREFIX}${delmanName}`)
}

export function isYesterdayDate(dateKey: string): boolean {
  return kitchenYesterdayISO() === dateKey
}

export function isStopInProgress(stop: DeliveryStop): boolean {
  if (stop.receivingStatus === 'completed') return false
  return stop.status === 'qr_shown' || stop.receivingStatus === 'draft'
}

export function getOrCreateTodayTrip(delmanName: string, kitchenName: string): TripRecord {
  const today = todayDateKey()
  const existing = loadJson<TripRecord>(DELMAN_TRIP_STORAGE_KEY)

  if (!isMockApi) {
    if (
      existing &&
      existing.date === today &&
      existing.delmanName === delmanName &&
      existing.status !== 'completed' &&
      !isDemoTrip(existing)
    ) {
      return existing
    }
    return createEmptyTodayTrip(delmanName, kitchenName)
  }

  if (
    existing &&
    existing.date === today &&
    existing.delmanName === delmanName &&
    existing.stops.length > 0 &&
    existing.status !== 'completed'
  ) {
    return existing
  }
  const trip = buildTripFromDispatch(delmanName, kitchenName)
  saveTripRecord(trip)
  return trip
}

export function getTripSummary(trip: TripRecord) {
  const completedStops = trip.stops.filter((s) => s.receivingStatus === 'completed').length
  const pendingReturns = trip.returnPickups.filter((r) => r.status === 'waiting_scan').length
  const pickedReturns = trip.returnPickups.filter((r) => r.status === 'picked_up').length
  const allDeliveriesDone = trip.stops.length > 0 && completedStops === trip.stops.length

  return {
    totalStops: trip.stops.length,
    completedStops,
    pendingReturns,
    pickedReturns,
    allDeliveriesDone,
  }
}

/** Urutan tampilan: belum selesai dulu, toko mulai trip di atas, lalu abjad. */
export function sortStopsForPicker(stops: DeliveryStop[], startedViaStopId?: string): DeliveryStop[] {
  return [...stops].sort((a, b) => {
    const aDone = a.receivingStatus === 'completed' ? 1 : 0
    const bDone = b.receivingStatus === 'completed' ? 1 : 0
    if (aDone !== bDone) return aDone - bDone
    if (startedViaStopId) {
      const aStarted = a.id === startedViaStopId ? 0 : 1
      const bStarted = b.id === startedViaStopId ? 0 : 1
      if (aStarted !== bStarted) return aStarted - bStarted
    }
    return a.storeName.localeCompare(b.storeName, 'id')
  })
}

export function getTripTotalShippedPcs(trip: TripRecord): number {
  return trip.stops.reduce(
    (sum, stop) => sum + stop.lines.reduce((lineSum, line) => lineSum + line.qtyShipped, 0),
    0,
  )
}

/** Estimasi sisa in transit dari selisih kirim vs terima. */
export function estimateTransitRemainder(trip: TripRecord): number {
  let remainder = 0
  for (const stop of trip.stops) {
    for (const line of stop.lines) {
      const received = line.qtyReceived ?? (stop.receivingStatus === 'completed' ? line.qtyShipped : 0)
      remainder += Math.max(0, line.qtyShipped - received)
    }
  }
  return remainder
}

export function stopStatusLabel(stop: DeliveryStop): string {
  if (stop.receivingStatus === 'completed') return 'Toko sudah terima'
  if (stop.receivingStatus === 'draft') return 'Toko menerima (draft)'
  if (stop.status === 'qr_shown') return 'Menunggu scan toko'
  return 'Belum handover'
}

export function stopBadgeClass(stop: DeliveryStop): string {
  if (stop.receivingStatus === 'completed') return 'badge-success'
  if (stop.receivingStatus === 'draft' || stop.status === 'qr_shown') return 'badge-warning'
  return 'badge-draft'
}

export type StopItineraryMeta = {
  icon: string
  iconClass: string
  lineClass: string
}

export function stopItineraryMeta(stop: DeliveryStop): StopItineraryMeta {
  if (stop.receivingStatus === 'completed') {
    return {
      icon: 'check_circle',
      iconClass: 'bg-status-success/20 text-status-success icon-fill text-[26px]',
      lineClass: 'border-status-success/40 bg-status-success/5',
    }
  }
  if (stop.receivingStatus === 'draft' || stop.status === 'qr_shown') {
    return {
      icon: 'schedule',
      iconClass: 'bg-status-warning/15 text-status-warning text-[26px]',
      lineClass: 'border-status-warning/40 bg-status-warning-bg/20',
    }
  }
  return {
    icon: 'storefront',
    iconClass: 'bg-primary/10 text-primary text-[26px]',
    lineClass: 'border-outline-variant bg-surface-card',
  }
}

export function stopActionLabel(stop: DeliveryStop, tripStatus: TripStatus): string {
  if (tripStatus === 'ready') return delmanTerms.trip.storeActionStartTrip
  if (stop.receivingStatus === 'completed') return delmanTerms.trip.storeActionSummary
  if (stop.receivingStatus === 'draft' || stop.status === 'qr_shown') {
    return delmanTerms.trip.storeActionMonitor
  }
  return delmanTerms.trip.storeActionViewDetail
}

/** Check-out absensi ditolak selama masih ada sesi kirim aktif (belum ditutup). */
export function blocksDelmanCheckout(trip: TripRecord): boolean {
  if (trip.stops.length === 0) return false
  return trip.status !== 'completed'
}
